import { useState, useCallback } from 'react';
import { doc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { db, getAllActivityLogs, getAllPatients } from '../firebase';
import { User } from '../types';
import { isSuperAdmin, canViewPatient } from '../utils/roleUtils';

export function useActivityLogs(
  user: User | null,
  showAlert: (message: string) => void
) {
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [activitySearchTerm, setActivitySearchTerm] = useState('');
  const [activityTimeFilter, setActivityTimeFilter] = useState<'all' | 'today' | 'lastWeek' | 'lastMonth'>('lastWeek');
  const [activityCurrentPage, setActivityCurrentPage] = useState(1);
  const [activitiesPerPage] = useState(10);

  const refreshActivityLogs = useCallback(async () => {
    if (!user) return;
    
    setIsActivityLoading(true);
    try {
      const activityData = await getAllActivityLogs();
      
      // Apply role-based filtering
      let filteredActivities = activityData;
      
      if (!isSuperAdmin(user)) {
        // Load user's programs and groups for permission checking
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        const userPrograms = programsSnapshot.docs
          .filter(doc => {
            const data = doc.data();
            return Array.isArray(data.managers) && data.managers.includes(user.id);
          })
          .map(doc => doc.id);
        
        const groupsSnapshot = await getDocs(collection(db, 'groups'));
        const userGroups = groupsSnapshot.docs
          .filter(doc => {
            const data = doc.data();
            return Array.isArray(data.managers) && data.managers.includes(user.id);
          })
          .map(doc => doc.id);
        
        // Get all patients to check which ones the user can view
        const allPatients = await getAllPatients();
        const viewablePatientIds = new Set(
          allPatients
            .filter(p => canViewPatient(user, p, userPrograms, userGroups))
            .map(p => p.caseId)
        );
        
        // Filter activities based on viewable patients
        filteredActivities = activityData.filter(a => {
          // Include activities for patients the user can view
          if (a.caseId && viewablePatientIds.has(a.caseId)) {
            return true;
          }
          // Also include activities without caseId that were created by the user
          // (for backward compatibility with legacy activities)
          if (!a.caseId && a.createdBy === user.id) {
            return true;
          }
          return false;
        });
      }
      
      setActivityLogs(filteredActivities);
    } catch (error) {
      console.error('Error refreshing activity logs:', error);
    } finally {
      setIsActivityLoading(false);
    }
  }, [user]);

  const handleDeleteActivityLog = useCallback(async (logId: string) => {
    try {
      const logRef = doc(db, 'activities', logId);
      await deleteDoc(logRef);
      
      // Refresh activity logs
      await refreshActivityLogs();
      
      console.log('Activity log deleted successfully');
    } catch (error) {
      console.error('Error deleting activity log:', error);
      showAlert('Failed to delete activity log. Please try again.');
    }
  }, [refreshActivityLogs, showAlert]);

  const getFilteredActivityLogs = useCallback(() => {
    let filtered = activityLogs;

    // Role-based filtering is already applied in refreshActivityLogs
    // No need to filter again here
    
    // Apply time filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    if (activityTimeFilter === 'today') {
      filtered = filtered.filter(log => {
        const logDate = log.timestamp && typeof log.timestamp === 'object' && log.timestamp.toDate
          ? log.timestamp.toDate()
          : log.timestamp
            ? new Date(log.timestamp)
            : null;
        return logDate && logDate >= today;
      });
    } else if (activityTimeFilter === 'lastWeek') {
      filtered = filtered.filter(log => {
        const logDate = log.timestamp && typeof log.timestamp === 'object' && log.timestamp.toDate
          ? log.timestamp.toDate()
          : log.timestamp
            ? new Date(log.timestamp)
            : null;
        return logDate && logDate >= lastWeek;
      });
    } else if (activityTimeFilter === 'lastMonth') {
      filtered = filtered.filter(log => {
        const logDate = log.timestamp && typeof log.timestamp === 'object' && log.timestamp.toDate
          ? log.timestamp.toDate()
          : log.timestamp
            ? new Date(log.timestamp)
            : null;
        return logDate && logDate >= lastMonth;
      });
    }

    // Apply search term filter
    if (activitySearchTerm) {
      const searchLower = activitySearchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        log.action?.toLowerCase().includes(searchLower) ||
        log.note?.toLowerCase().includes(searchLower) ||
        log.userEmail?.toLowerCase().includes(searchLower) ||
        log.createdBy?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [activityLogs, activitySearchTerm, activityTimeFilter, user]);

  const getPaginatedActivityLogs = useCallback(() => {
    const filtered = getFilteredActivityLogs();
    const startIndex = (activityCurrentPage - 1) * activitiesPerPage;
    return filtered.slice(startIndex, startIndex + activitiesPerPage);
  }, [getFilteredActivityLogs, activityCurrentPage, activitiesPerPage]);

  return {
    activityLogs,
    setActivityLogs,
    isActivityLoading,
    activitySearchTerm,
    setActivitySearchTerm,
    activityTimeFilter,
    setActivityTimeFilter,
    activityCurrentPage,
    setActivityCurrentPage,
    activitiesPerPage,
    handleDeleteActivityLog,
    getFilteredActivityLogs,
    getPaginatedActivityLogs,
    refreshActivityLogs
  };
}


